import { useEffect, useState } from 'react';
import { Alert, FormControl, HelpBlock } from 'react-bootstrap';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'react-toastify';
import { IOpoAdminSettings, IOpoAdminSettingsRequest } from '@nkr/models/opo-admin-settings';
import { IOpoAdminSaveResult } from '@nkr/store/opo/hooks/use-opo-admin-settings';

const routingNumber = z.string().trim().refine(value => value == '' || /^\d{9}$/.test(value), 'Routing number must be 9 digits');
const accountNumber = z.string().trim().refine(value => value == '' || /^\d{4,17}$/.test(value), 'Account number must be 4 to 17 digits');

const billingSchema = z.object({
    achRoutingNumber: routingNumber,
    achAccountNumber: accountNumber,
    achApproved: z.boolean(),
    isActive: z.boolean()
});

type BillingFormValues = z.infer<typeof billingSchema>;

const toFormValues = (settings: IOpoAdminSettings): BillingFormValues => ({
    achRoutingNumber: settings.achRoutingNumber,
    achAccountNumber: '',
    achApproved: settings.achApproved,
    isActive: settings.isActive
});

type TextField = 'achRoutingNumber' | 'achAccountNumber';

interface IProps {
    settings: IOpoAdminSettings,
    onSave: (request: IOpoAdminSettingsRequest) => Promise<IOpoAdminSaveResult>
}

const BillingSettings = ({ settings, onSave }: IProps) => {
    const [serverError, setServerError] = useState('');
    const { control, handleSubmit, reset, formState: { errors, isSubmitting, isDirty } } = useForm<BillingFormValues>({
        resolver: zodResolver(billingSchema),
        defaultValues: toFormValues(settings)
    });

    useEffect(() => {
        reset(toFormValues(settings));
    }, [settings]);

    const onSubmit = async (values: BillingFormValues) => {
        setServerError('');

        const result = await onSave({
            achRoutingNumber: values.achRoutingNumber,
            achAccountNumber: values.achAccountNumber,
            achApproved: values.achApproved,
            isActive: values.isActive
        });

        if (result.success) {
            toast.success('Billing settings saved');
        } else {
            setServerError(result.message);
        }
    };

    const renderTextRow = (name: TextField, label: string, maxLength: number, maskedValue?: string) => (
        <div className={errors[name] ? 'settings-row has-error' : 'settings-row'}>
            <label className='control-label' htmlFor={name}>{label}</label>
            <div>
                <Controller
                    control={control}
                    name={name}
                    render={({ field: { value, onChange, onBlur } }) => (
                        <FormControl
                            id={name}
                            type='text'
                            inputMode='numeric'
                            autoComplete='off'
                            maxLength={maxLength}
                            placeholder={maskedValue || ''}
                            value={value}
                            onChange={onChange}
                            onBlur={onBlur}
                        />
                    )}
                />
                {errors[name] && (
                    <HelpBlock>{errors[name].message}</HelpBlock>
                )}
                {maskedValue != null && (
                    <div className='settings-hint'>{maskedValue ? 'Leave blank to keep the current account number' : 'No account number on file'}</div>
                )}
            </div>
        </div>
    );

    const renderYesNoRow = (name: 'achApproved' | 'isActive', label: string, hint?: string) => (
        <div className='settings-row'>
            <label className='control-label'>{label}</label>
            <div>
                <Controller
                    control={control}
                    name={name}
                    render={({ field: { value, onChange } }) => (
                        <div className='segmented' role='radiogroup' aria-label={label}>
                            <button type='button' role='radio' aria-checked={value} className={value ? 'btn active' : 'btn'} onClick={() => onChange(true)}>Yes</button>
                            <button type='button' role='radio' aria-checked={!value} className={!value ? 'btn active' : 'btn'} onClick={() => onChange(false)}>No</button>
                        </div>
                    )}
                />
                {hint && (
                    <div className='settings-hint'>{hint}</div>
                )}
            </div>
        </div>
    );

    const approvedHint = settings.achApproved && settings.achApprovedByName
        ? `Approved by ${settings.achApprovedByName}${settings.achApprovedDateTime ? ` on ${settings.achApprovedDateTime}` : ''}`
        : undefined;

    return (
        <form className='settings-card' onSubmit={handleSubmit(onSubmit)} noValidate>
            <div className='settings-card-header'>
                <h4>Billing Settings</h4>
            </div>

            <div className='settings-card-body'>
                {serverError && (
                    <Alert bsStyle='danger'>{serverError}</Alert>
                )}

                {renderTextRow('achRoutingNumber', 'ABA Routing Number', 9)}
                {renderTextRow('achAccountNumber', 'Bank Account Number', 17, settings.achAccountNumberMasked)}

                <div className='settings-subsection'>Status</div>

                {renderYesNoRow('achApproved', 'ACH Approved', approvedHint)}
                {renderYesNoRow('isActive', 'Active')}
            </div>

            <div className='settings-card-footer'>
                <button type='submit' className='btn btn-primary' disabled={isSubmitting || !isDirty}>Save Changes</button>
                <button type='button' className='btn' disabled={isSubmitting || !isDirty} onClick={() => { reset(toFormValues(settings)); setServerError(''); }}>Cancel</button>
            </div>
        </form>
    );
}

export default BillingSettings;
